import { ReactNode, useCallback, useState } from 'react';
import { getDefaultValue, Listener, LocalStorageKey, LocalStorageType } from '../data/client_local_storage.ts';
import { LocalStorageContext } from '../data/use_local_storage.tsx';

const listeners: Partial<{ [key in LocalStorageKey]: Listener[] }> = {};

function addListenerCtx<T extends LocalStorageKey>(key: T, listener: Listener): void {
  if (key in listeners) {
    listeners[key]!.push(listener);
  } else {
    listeners[key] = [listener];
  }
}

function removeListenerCtx<T extends LocalStorageKey>(key: T, listener: Listener): void {
  const keyListeners = listeners[key];
  if (!keyListeners) {
    return;
  }
  const index = keyListeners.indexOf(listener);
  if (index !== -1) {
    keyListeners.splice(index, 1);
  }
}

export type LocalStorageMockValues = Partial<{ [key in LocalStorageKey]: LocalStorageType<key> }>;

export const LocalStorageMockProvider = ({
  values,
  children,
}: {
  values: LocalStorageMockValues;
  children: ReactNode;
}) => {
  const [valueOverrides] = useState<{ values: { [key in LocalStorageKey]: LocalStorageType<key> } }>(() => ({
    values: {} as any,
  }));
  const localStorageGetCtx = useCallback(
    <T extends LocalStorageKey>(key: T, defaultOverride?: LocalStorageType<T>): LocalStorageType<T> => {
      return valueOverrides.values[key] ?? values[key] ?? defaultOverride ?? getDefaultValue(key);
    },
    [valueOverrides, values],
  );
  const localStorageSetCtx = useCallback(
    <T extends LocalStorageKey>(key: T, value: LocalStorageType<T>): void => {
      valueOverrides.values[key] = value;
      // like the real store, a write re-renders every useLocalStorage subscriber of the key
      for (const listener of [...(listeners[key] ?? [])]) {
        listener();
      }
    },
    [valueOverrides],
  );
  return (
    <LocalStorageContext value={{ localStorageGetCtx, localStorageSetCtx, addListenerCtx, removeListenerCtx }}>
      {children}
    </LocalStorageContext>
  );
};
